import React, { memo, useState } from 'react';
import { ProgressOverlay } from '../../common/progress-overlay/ProgressOverlay';
import styles from './DocumentDetails.module.scss';
import { Box, Button, Grid, IconButton, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import AISummaryDetails from './components/ai-summary-details/AISummaryDetails';
import DocumentContent from './components/document-content/DocumentContent';
import DocumentDetailsBlock from './components/document-details/DocumentDetailsBlock';
import { useDocumentItem } from './hooks/useDocumentItem';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ROUTES } from '../../common/constants/routes';
import { sanitizeRedirectUrl } from '../../common/helpers/sanitize';
import { useDocumentDetails } from './hooks/useDocumentDetails';
import { useAppSelector } from '../../common/hooks/useAppSelector';
import { useDocumentHTML } from './hooks/useDocumentHTML';
import { useDocumentSigning } from '../document-vault/hooks/useDocumentSigning';
import MessageModal from '../../common/message-modal/MessageModal';
import FileTypeDownloadModal from '../../common/file-type-download-modal/FileTypeDownloadModal';
import { useDeleteMessageModal } from '../document-vault/hooks/useDeleteMessageModal';
import { useFileTypeDownload } from '../../common/file-type-download-modal/useFileTypeDownload';
import { useTemplateCreation } from './hooks/useTemplateCreation';
import { useLatestVersion } from './hooks/useLatestVersion';
import BrushIcon from '../../../assets/svg/brush.svg?react';
import { useDocumentsFunctions } from '../document-vault/hooks/useDocumentsFunctions';
import Checkbox from '@mui/material/Checkbox';
import { AssignProfilePersonModal } from '../../common/assign-profile-person-modal/AssignProfilePersonModal';
import { showOverlay } from '../../slices/show-progress-overlay/showProgressOverlay';
import { useAssignProfilePersonModal } from '../../common/assign-profile-person-modal/useAssignProfilePersonModal';
import { useAppDispatch } from '../../common/hooks/useAppDispatch';
import { selectAllEmployees } from '../../slices/employees/employeesSlice';
import { selectAllCustomers } from '../../slices/customers/customersSlice';
import {
  BusinessProfileContacts,
  selectBusinessProfileContacts,
} from '../../slices/business-profile/businessProfileSlice';
import { selectAllSuppliers } from '../../slices/suppliers/suppliersSlice';
import { Employee } from '../../models/employee.model';
import { Customer } from '../../models/customer.model';
import { Supplier } from '../../models/supplier.model';
import { Helpers } from '../../common/helpers/helpers';
import { useDocumentContent } from './hooks/useDocumentContent';
import { PacmanLoaderBlock } from '../../common/pacman-loader';
import ProgressBarFancy from '../../common/fancy-progress-bar';
import { clearPlaceholdersIds } from '../../slices/templatePlaceholdersIds/templatePlaceholdersIds';
import { clearPlaceholdersIndexes } from '../../slices/templatePlaceholdersIndexes/templatePlaceholdersIndexes';
import { useHasClerkRole } from '../../common/hooks/useHasClerkRole';
import { useCheckPermission } from '../../common/hooks/useCheckPermission';
import { USER_PERMISSIONS } from '../../common/constants/permissions';
import { useDocumensoRedirect } from '../../common/hooks/useDocumensoRedirect';

const DocumentDetails = () => {
  const { t } = useTranslation();
  const { activeTab, handleTabChange } = useDocumentItem();
  const { documentId } = useParams();
  const dispatch = useAppDispatch();
  const [params] = useSearchParams();
  const contractGenerated = params.get('contractGenerated');
  const generateTemplateParam = params.get('generateTemplate');
  const backURL = params.get('backURL');
  const location = useLocation();
  const path = location.pathname;
  const navigate = useNavigate();
  const { checkedLatestVersion } = useLatestVersion();
  const employeesStatus = useAppSelector((state) => state.employees.status);
  const suppliersStatus = useAppSelector((state) => state.suppliers.status);
  const customersStatus = useAppSelector((state) => state.customers.status);
  const businessContactsStatus = useAppSelector((state) => state.businessProfile.status);
  const userIsEmployee = useHasClerkRole('org:employee');
  const { redirectToDocumenso, isLoading: False } = useDocumensoRedirect();
  const detailsProps = {
    ...useDocumentDetails({
      id: Number(documentId),
      isDocumentContract: Boolean(contractGenerated),
      allowedCreatingHTML: activeTab === 0,
    }),
  };
  const { navigateToDocumentPageTab } = useDocumentsFunctions();
  const htmlArray = contractGenerated
    ? null
    : !detailsProps.documentUploadLatestHTMLId && detailsProps.documentDetails
      ? detailsProps.documentDetails.document_upload_html
      : detailsProps.documentUploadLatestHTMLId
        ? [detailsProps.documentUploadLatestHTMLId]
        : [];
  const {
    documentHTML,
    setDocumentHTML,
    saveDocumentHTML,
    pendingStatus: pendingStatusHTML,
    contentEditable,
    setContentEditable,
    showConfirmationModal,
    setShowConfirmationModal,
  } = useDocumentHTML(
    htmlArray,
    activeTab === 0,
    detailsProps.documentDetails && 'contract_generation' in detailsProps.documentDetails
      ? detailsProps.documentDetails.html
      : null
  );

  const dataFetching =
    detailsProps.loadingData ||
    suppliersStatus === 'loading' ||
    customersStatus === 'loading' ||
    employeesStatus === 'loading' ||
    businessContactsStatus === 'loading';

  const {
    document: documentToSign,
    setDocument: setDocumentToSign,
    handleSignOriginalDocument,
    handleSignLatestVersion,
    setOpenAssignPersonDialog,
    openAssignPersonDialog,
  } = useDocumentSigning();
  const { patchDocumentWithProfilePerson, setAssignee, assignee } = useAssignProfilePersonModal(
    documentToSign?.category,
    documentToSign
  );
  const {
    setModalOpen: setDeleteMessageModalOpen,
    modalOpen: deleteMessageModalOpen,
    pendingStatus: documentDeletePendingStatus,
    documentToDelete,
    setDocumentToDelete,
    handleDeleteDocument,
  } = useDeleteMessageModal();
  const {
    setModalOpen: setFileTypeModalOpen,
    modalOpen: fileTypeModalOpen,
    handleCloseModal: handleCloseFileTypeModal,
    setDocument: setDocumentForDownloading,
    document: documentForDownloading,
    setFileName: setFileNameForDownloading,
    fileName: fileNameForDownloading,
    fileType: fileTypeForDownloading,
    setFileType: setFileTypeForDownloading,
    handleDownloadContract,
    downloadDocumentUploadItem,
    handleDownloadHTML,
    pendingStatus: downloadPendingStatus,
  } = useFileTypeDownload();
  const [pdfSaveDialogOpen, setPdfSaveDialogOpen] = useState(false);
  const {
    templateCreationEnabled,
    setTemplateCreationEnabled,
    pendingStatus: templateCreationPending,
    template,
    creatingTemplate,
    setTemplate,
    setDocumentTemplate,
    deletePlaceholders,
    addTemplatePlaceholderRow,
    templateCreationStep,
    setTemplateCreationStep,
    setDeletePlaceholdersMessageVisible,
    deletePlaceholdersMessageVisible,
    templateName,
    setTemplateName,
    category,
    setCategory,
    documentTypeLabel,
    setDocumentTypeLabel,
    documentWorkflowLabel,
    setDocumentWorkflowLabel,
    customLabel,
    setCustomLabel,
    categorySelectValues,
    getTypeLabels,
    workflowsArray,
    placeholderCheckUncheck,
  } = useTemplateCreation(
    Number(documentId),
    Boolean(contractGenerated),
    contractGenerated
      ? null
      : detailsProps?.documentDetails &&
          detailsProps?.documentDetails?.document_upload_html?.length > 0
        ? Math[checkedLatestVersion ? 'max' : 'min'](
            // eslint-disable-next-line no-unsafe-optional-chaining
            ...detailsProps?.documentDetails?.document_upload_html
          )
        : detailsProps?.documentUploadLatestHTMLId,
    detailsProps?.documentDetails && detailsProps?.documentDetails?.name,
    detailsProps?.documentDetails,
    documentHTML
  );
  const { fileBlobURL, fileLoading, pdfBlobMode, setPDFBlobMode } = useDocumentContent(
    detailsProps?.documentDetails?.id,
    detailsProps?.documentDetails && detailsProps?.documentDetails?.du_filetype
      ? detailsProps?.documentDetails?.du_filetype[0]
      : null
  );
  const employees = useAppSelector(selectAllEmployees);
  const customers = useAppSelector(selectAllCustomers);
  const businessContacts = useAppSelector(selectBusinessProfileContacts);
  const suppliers = useAppSelector(selectAllSuppliers);
  const checkPermission = useCheckPermission();
  const checkDocumentEditingPermission = () => {
    switch (detailsProps?.category) {
      case 'employment':
        return checkPermission(USER_PERMISSIONS.ORG_EMPLOYEE_PROFILE_UPDATE_DOCUMENT);
      case 'business':
        return checkPermission(USER_PERMISSIONS.ORG_BUSINESS_PROFILE_UPDATE_DOCUMENT);
      case 'customers':
        return checkPermission(USER_PERMISSIONS.ORG_CUSTOMER_PROFILE_UPDATE_DOCUMENT);
      case 'suppliers':
        return checkPermission(USER_PERMISSIONS.ORG_SUPPLIER_PROFILE_UPDATE_DOCUMENT);
    }
  };
  const workflowsProperty = detailsProps?.documentDetails?.contract_generation
    ? 'dc_workflows'
    : 'du_workflows';

  const checkProfilePersonsAvailability = (category: string) => {
    switch (category) {
      case 'employment':
        return employees?.length > 0;
      case 'business':
        return false;
      case 'customers':
        return customers?.length > 0;
      case 'suppliers':
        return false;
    }
  };
  const documentFromProfileEntity = [
    ROUTES.SUPPLIER_PROFILES,
    ROUTES.EMPLOYEE_PROFILES,
    ROUTES.CUSTOMER_PROFILES,
    ROUTES.BUSINESS_PROFILE,
  ].some((el) => backURL?.includes(el));
  const documentFromKeyDates = [ROUTES.DOCUMENTS_KEY_DATES].some((el) => backURL?.includes(el));
  const categoryName = (category: string) => {
    switch (category) {
      case 'employment':
        return t('Employees');
      case 'customers':
        return t('Customers');
      case 'business':
        return t('Business');
      case 'suppliers':
        return t('Suppliers');
      default:
        return '';
    }
  };

  const determineProfileRoutes = (category: string) => {
    switch (category) {
      case 'employment':
        return ROUTES.EMPLOYEE_PROFILES;
      case 'customers':
        return ROUTES.CUSTOMER_PROFILES;
      case 'business':
        return ROUTES.BUSINESS_PROFILE;
      case 'suppliers':
        return ROUTES.SUPPLIER_PROFILES;
      default:
        return null;
    }
  };

  const profileLabelsProp = (category: string) => {
    if (category) {
      switch (category) {
        case 'employment':
          return 'employees';
        case 'suppliers':
          return 'suppliers';
        case 'customers':
          return 'customers';
        case 'business':
          return 'business_profile_contacts';
      }
    }
  };

  const profileNameProp = (category: string) => {
    if (category) {
      switch (category) {
        case 'employment':
          return 'full_name';
        case 'suppliers':
          return 'name';
        case 'customers':
          return 'customer_name';
        case 'business':
          return 'contact_name';
      }
    }
  };

  const profileItems = (
    category: string
  ): Employee[] | Supplier[] | Customer[] | BusinessProfileContacts[] => {
    switch (category) {
      case 'employment':
        return employees;
      case 'suppliers':
        return suppliers;
      case 'customers':
        return customers;
      case 'business':
        return businessContacts;
    }
  };

  const profileEntityName = () => {
    const category = detailsProps?.documentDetails?.category;
    const item = category
      ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        profileItems(category).find(
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          (el) => el.id === detailsProps?.documentDetails[profileLabelsProp(category)][0]
        )
      : null;
    return item ? item[profileNameProp(category)] : '';
  };

  const documentIsSubscription = () => {
    const typeProperty =
      detailsProps &&
      detailsProps.documentDetails &&
      'contract_generation' in detailsProps.documentDetails
        ? 'dc_types'
        : 'du_types';
    return (
      detailsProps?.documentDetails?.[typeProperty].length > 0 &&
      detailsProps?.documentDetails?.[typeProperty][0] === 'Subscription'
    );
  };

  return (
    <>
      <Box>
        <Grid container className={styles.header}>
          <Grid size={{ xs: 10 }}>
            <Typography variant='h5' component='h1' className={styles.title}>
              {path.includes(ROUTES.WORKSPACE)
                ? t('headers.workspace')
                : t('headers.documentVault')}
            </Typography>
            <Box className={styles.breadcrumbs}>
              {backURL ? (
                documentFromProfileEntity ? (
                  <>
                    {!userIsEmployee && (
                      <>
                        <Link
                          className={styles.documentViewText}
                          to={determineProfileRoutes(detailsProps?.documentDetails?.category)}
                        >
                          {detailsProps?.documentDetails
                            ? categoryName(detailsProps?.documentDetails?.category)
                            : ''}
                        </Link>
                        <Typography variant='body2' className={styles.separator}>
                          ›
                        </Typography>
                      </>
                    )}
                    {detailsProps?.documentDetails?.category !== 'business' && (
                      <>
                        <Link
                          className={styles.documentViewText}
                          to={
                            detailsProps?.documentDetails
                              ? `${determineProfileRoutes(
                                  detailsProps?.documentDetails?.category
                                )}/${
                                  detailsProps?.documentDetails[
                                    profileLabelsProp(detailsProps?.documentDetails?.category)
                                  ]
                                }`
                              : null
                          }
                        >
                          {detailsProps?.documentDetails &&
                            profileItems(detailsProps?.documentDetails?.category) &&
                            profileEntityName()}
                        </Link>
                        <Typography variant='body2' className={styles.separator}>
                          ›
                        </Typography>
                      </>
                    )}
                    <Link
                      className={styles.documentViewText}
                      to={
                        detailsProps?.documentDetails
                          ? `${determineProfileRoutes(detailsProps?.documentDetails?.category)}/${
                              detailsProps?.documentDetails?.category !== 'business'
                                ? detailsProps?.documentDetails[
                                    profileLabelsProp(detailsProps?.documentDetails?.category)
                                  ]
                                : ''
                            }?workflow=${
                              detailsProps?.documentDetails[workflowsProperty] &&
                              detailsProps?.documentDetails[workflowsProperty].length > 0
                                ? detailsProps.documentDetails[workflowsProperty][0]
                                : t('headers.general')
                            }`
                          : null
                      }
                    >
                      {detailsProps?.documentDetails
                        ? detailsProps?.documentDetails[workflowsProperty] &&
                          detailsProps.documentDetails[workflowsProperty].length > 0
                          ? detailsProps.documentDetails[workflowsProperty][0]
                          : t('labels.generalAdministration')
                        : ''}
                    </Link>
                    <Typography variant='body2' className={styles.separator}>
                      ›
                    </Typography>
                    <Typography variant='body2' className={styles.documentViewText}>
                      {detailsProps.documentDetails ? detailsProps.documentDetails.name : ''}
                    </Typography>
                  </>
                ) : documentFromKeyDates ? (
                  <>
                    <Link
                      className={styles.documentViewText}
                      to={
                        ROUTES.DOCUMENTS_KEY_DATES +
                        `${documentIsSubscription() ? '/?activeTab=Subscriptions' : ''}`
                      }
                    >
                      {t('headers.keyDates')}
                    </Link>
                    <Typography variant='body2' className={styles.separator}>
                      ›
                    </Typography>
                    <Typography variant='body2' className={styles.documentViewText}>
                      {detailsProps.documentDetails ? detailsProps.documentDetails.name : ''}
                    </Typography>
                  </>
                ) : (
                  <Link className={styles.documentViewText} to={sanitizeRedirectUrl(backURL)}>
                    {t('buttons.back')}
                  </Link>
                )
              ) : (
                <>
                  <Link
                    className={styles.documentViewText}
                    to={path.includes(ROUTES.WORKSPACE) ? ROUTES.WORKSPACE : ROUTES.DOCUMENT_VAULT}
                  >
                    {path.includes(ROUTES.WORKSPACE)
                      ? t('headers.workspace')
                      : t('headers.documentVault')}
                  </Link>
                  <Typography variant='body2' className={styles.separator}>
                    ›
                  </Typography>
                  <Link
                    className={styles.documentViewText}
                    to={
                      path.includes(ROUTES.WORKSPACE)
                        ? ROUTES.WORKSPACE + `?activeTab=${detailsProps?.documentDetails?.category}`
                        : ROUTES.DOCUMENT_VAULT +
                          `?activeTab=${detailsProps?.documentDetails?.category}`
                    }
                  >
                    {detailsProps?.documentDetails
                      ? Helpers.capitalizeFirstLetter(detailsProps?.documentDetails?.category)
                      : ''}
                  </Link>
                  <Typography variant='body2' className={styles.separator}>
                    ›
                  </Typography>
                  <Typography variant='body2' className={styles.documentViewText}>
                    {detailsProps.documentDetails ? detailsProps.documentDetails.name : ''}
                  </Typography>
                </>
              )}
            </Box>
          </Grid>
        </Grid>
        <Grid container justifyContent='space-between' spacing={2} className='inputs-editable'>
          {activeTab === 0 ? (
            <Grid size={{ xs: 12 }} className={styles.gridItem}>
              <Tabs value={activeTab} onChange={handleTabChange} className='tabs-document-details'>
                <Tab label={t('tabs.documentContent')} className={styles.tab} />
                <Tab label={t('tabs.documentDetails')} className={styles.tab} />
              </Tabs>
              <Box className={styles.tabContent}>
                <DocumentContent
                  placeholderCheckUncheck={placeholderCheckUncheck}
                  documentHTML={documentHTML}
                  templateName={templateName}
                  setTemplateName={setTemplateName}
                  category={category}
                  setCategory={setCategory}
                  documentTypeLabel={documentTypeLabel}
                  setDocumentTypeLabel={setDocumentTypeLabel}
                  documentWorkflowLabel={documentWorkflowLabel}
                  setDocumentWorkflowLabel={setDocumentWorkflowLabel}
                  customLabel={customLabel}
                  setCustomLabel={setCustomLabel}
                  categoryArray={categorySelectValues}
                  typeLabels={getTypeLabels()}
                  workflowsArray={workflowsArray()}
                  deletePlaceholdersMessageVisible={deletePlaceholdersMessageVisible}
                  setDeletePlaceholdersMessageVisible={setDeletePlaceholdersMessageVisible}
                  templateCreationStep={templateCreationStep}
                  setTemplateCreationStep={setTemplateCreationStep}
                  isImage={detailsProps.isImage}
                  contentEditable={contentEditable}
                  pdfBlobMode={pdfBlobMode}
                  setPDFBlobMode={setPDFBlobMode}
                  fileBlobURL={fileBlobURL}
                  fileLoading={fileLoading}
                  generateAISummary={() => {
                    handleTabChange(null, 1);
                    navigate(
                      location.pathname +
                        `?initialTab=1&generateSummary=true${
                          'contract_generation' in detailsProps.documentDetails
                            ? '&contractGenerated=true'
                            : ''
                        }`
                    );
                  }}
                  downloadDocument={() => {
                    if ('contract_generation' in detailsProps.documentDetails) {
                      setDocumentForDownloading(detailsProps.documentDetails);
                      setFileNameForDownloading(detailsProps.documentDetails.name);
                      setFileTypeModalOpen(true);
                    } else {
                      if (
                        detailsProps.documentDetails.document_upload_html.length > 0 ||
                        detailsProps.documentUploadLatestHTMLId
                      ) {
                        setDocumentForDownloading(detailsProps.documentDetails);
                        setFileTypeModalOpen(true);
                      } else {
                        downloadDocumentUploadItem(
                          detailsProps.documentDetails.id,
                          detailsProps.documentDetails.name
                        );
                      }
                    }
                  }}
                  signDocument={() => {
                    setDocumentToSign(detailsProps.documentDetails);
                    if (detailsProps.documentDetails.signing_doc_id) {
                      redirectToDocumenso(
                        `${ROUTES.DOCUMENTS}/${detailsProps.documentDetails.signing_doc_id}?view=true&backUrl=${encodeURIComponent(sanitizeRedirectUrl(path))}`
                      );
                    } else {
                      if (
                        (detailsProps.documentDetails.customers.length === 0 &&
                          detailsProps.documentDetails.employees.length === 0 &&
                          checkProfilePersonsAvailability(detailsProps.documentDetails.category)) ||
                        ('document_upload_html' in detailsProps.documentDetails &&
                          detailsProps.documentDetails.document_upload_html.length > 0)
                      ) {
                        setDocumentToSign(detailsProps.documentDetails);
                        setOpenAssignPersonDialog(true);
                      } else {
                        dispatch(showOverlay());
                        handleSignOriginalDocument(detailsProps.documentDetails);
                      }
                    }
                  }}
                  deleteDocument={() => {
                    setDocumentToDelete(detailsProps.documentDetails);
                    setDeleteMessageModalOpen(true);
                  }}
                  editDocument={() => {
                    if (
                      detailsProps?.documentDetails &&
                      detailsProps?.documentDetails?.du_filetype &&
                      detailsProps?.documentDetails?.du_filetype[0] === 'pdf' //&&
                      //detailsProps?.documentDetails?.document_upload_html.length === 0 &&
                      //!detailsProps?.documentUploadLatestHTMLId
                    ) {
                      if (localStorage.getItem('showPDFConversionMessage') === 'true') {
                        detailsProps.setPDFConversionMessage(true);
                      } else {
                        if (
                          detailsProps?.documentDetails?.document_upload_html.length === 0 &&
                          !detailsProps?.documentUploadLatestHTMLId
                        ) {
                          detailsProps.setPDFDocHTMLCreationAllowed(true);
                        }
                      }
                    }
                  }}
                  saveDocument={saveDocumentHTML}
                  setDocumentHTML={setDocumentHTML}
                  createTemplate={() => {
                    setTemplateCreationEnabled(true);
                    if (
                      detailsProps?.documentDetails &&
                      detailsProps?.documentDetails?.du_filetype &&
                      detailsProps?.documentDetails?.du_filetype[0] === 'pdf' //&&
                      //detailsProps?.documentDetails?.document_upload_html.length === 0 &&
                      //!detailsProps?.documentUploadLatestHTMLId
                    ) {
                      if (localStorage.getItem('showPDFConversionMessage') === 'true') {
                        detailsProps.setPDFConversionMessage(true);
                      } else {
                        if (
                          detailsProps?.documentDetails?.document_upload_html.length === 0 &&
                          !detailsProps?.documentUploadLatestHTMLId
                        ) {
                          detailsProps.setPDFDocHTMLCreationAllowed(true);
                        }
                      }
                    }
                  }}
                  doc={detailsProps.documentDetails}
                  templateCreationEnabled={templateCreationEnabled}
                  templatePlaceholders={template}
                  createTemplateFromDoc={() => creatingTemplate()}
                  goBack={() => {
                    setTemplateCreationEnabled(false);
                    setDocumentTemplate(null);
                    setTemplate([]);
                    dispatch(clearPlaceholdersIds());
                    dispatch(clearPlaceholdersIndexes());
                    if (generateTemplateParam && detailsProps) {
                      navigateToDocumentPageTab(detailsProps.documentDetails, 0);
                    }
                  }}
                  setContentEditable={setContentEditable}
                  /*setContentEditable={() => {
                                      setContentEditable;
                                      /*if (
                                        detailsProps?.documentDetails &&
                                        detailsProps?.documentDetails?.du_filetype &&
                                        detailsProps?.documentDetails?.du_filetype[0] === 'pdf' //&&
                                        /*detailsProps?.documentDetails?.document_upload_html.length === 0 &&*/
                  //!detailsProps?.documentUploadLatestHTMLId
                  /* ) {
                                        if (localStorage.getItem('showPDFConversionMessage') === 'true') {
                                          detailsProps.setPDFConversionMessage(true);
                                        } else {
                                          detailsProps.setPDFDocHTMLCreationAllowed(true);
                                        }
                                      }*/
                  /*}}*/
                  showConfirmationModal={showConfirmationModal}
                  setShowConfirmationModal={setShowConfirmationModal}
                  deletePlaceholders={deletePlaceholders}
                  documentHTMLId={
                    !contractGenerated &&
                    detailsProps &&
                    detailsProps.documentDetails &&
                    detailsProps.documentDetails.document_upload_html
                      ? Math[checkedLatestVersion ? 'max' : 'min'](
                          // eslint-disable-next-line no-unsafe-optional-chaining
                          ...detailsProps?.documentDetails?.document_upload_html
                        )
                      : null
                  }
                  addTemplatePlaceholderRow={addTemplatePlaceholderRow}
                />
              </Box>
              {(pendingStatusHTML || detailsProps.loadingData) && !templateCreationPending && (
                <ProgressOverlay className='position-absolute' />
              )}
              {templateCreationPending &&
                !detailsProps.pdfConversionMessage &&
                !detailsProps.pendingStatus &&
                !pendingStatusHTML &&
                !dataFetching &&
                !detailsProps.loadingData && (
                  <PacmanLoaderBlock loading={templateCreationPending} />
                )}
            </Grid>
          ) : (
            <>
              <Grid
                size={{ xs: 5 }}
                className={`${styles.gridItem} ${
                  detailsProps.propertiesEditMode ? styles.card_border : ''
                }`}
                sx={{ position: 'relative' }}
              >
                <Box
                  display='flex'
                  paddingBottom='8px'
                  justifyContent='space-between'
                  alignItems='flex-start'
                  gap='16px'
                >
                  <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    className='tabs-document-details'
                  >
                    <Tab label={t('tabs.documentContent')} className={styles.tab} />
                    <Tab label={t('tabs.documentDetails')} className={styles.tab} />
                  </Tabs>
                  {detailsProps.propertiesEditMode ? (
                    <Box display='flex' gap='8px' alignItems='center'>
                      <Button
                        type='button'
                        className='btn-default btn-action-basic btn-cancel'
                        role='button'
                        variant='contained'
                        size='large'
                        onClick={() => {
                          detailsProps.setPropertiesEditMode(false);
                          detailsProps.setDetailsUpdated(false);
                        }}
                      >
                        {t('buttons.cancel')}
                      </Button>
                      <Button
                        type='button'
                        className='btn-default btn-action-basic'
                        role='button'
                        variant='contained'
                        onClick={() => {
                          detailsProps.updateDocumentItem();
                          detailsProps.setPropertiesEditMode(false);
                          detailsProps.setDetailsUpdated(true);
                        }}
                      >
                        {t('buttons.save')}
                      </Button>
                    </Box>
                  ) : (
                    <Tooltip
                      title={
                        userIsEmployee && !checkDocumentEditingPermission()
                          ? t('messages.contactAdministrator')
                          : t('buttons.edit')
                      }
                      placement='top'
                      arrow
                    >
                      <IconButton
                        size='small'
                        className={styles.editButton}
                        disabled={userIsEmployee && !checkDocumentEditingPermission()}
                        onClick={
                          userIsEmployee && !checkDocumentEditingPermission()
                            ? null
                            : () => detailsProps.setPropertiesEditMode(true)
                        }
                      >
                        <BrushIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                <Box
                  className={styles.tabContent}
                  sx={{ height: `calc(100vh - 205px)`, overflow: dataFetching ? 'hidden' : 'auto' }}
                >
                  <DocumentDetailsBlock
                    {...detailsProps}
                    loadingData={dataFetching}
                    setCategory={detailsProps.setCategory}
                    profileLabelsArray={detailsProps.profileLabelsArray(detailsProps.category)}
                    workflowsArray={detailsProps.workflowsArray()}
                    categoryArray={detailsProps.categorySelectValues}
                    typeLabels={detailsProps.getTypeLabels()}
                  />
                </Box>
                {dataFetching && <ProgressOverlay className={'position-absolute'} />}
              </Grid>
              <Grid size={{ xs: 7 }}>
                <Box sx={{ height: `calc(100vh - 128px)` }} className={styles.gridItemBoxShadow}>
                  <AISummaryDetails
                    isImage={detailsProps.isImage}
                    downloadDocument={() => {
                      if (detailsProps.documentDetails.contract_generation) {
                        setDocumentForDownloading(detailsProps.documentDetails);
                        setFileNameForDownloading(detailsProps.documentDetails.name);
                        setFileTypeModalOpen(true);
                      } else {
                        if (
                          detailsProps?.documentDetails.document_upload_html.length > 0 ||
                          detailsProps?.documentUploadLatestHTMLId
                        ) {
                          setDocumentForDownloading(detailsProps.documentDetails);
                          setFileTypeModalOpen(true);
                        } else {
                          downloadDocumentUploadItem(
                            detailsProps.documentDetails.id,
                            detailsProps.documentDetails.name
                          );
                        }
                      }
                    }}
                    signDocument={() => {
                      setDocumentToSign(detailsProps.documentDetails);
                      if (detailsProps.documentDetails.signing_doc_id) {
                        redirectToDocumenso(
                          `${ROUTES.DOCUMENTS}/${detailsProps.documentDetails.signing_doc_id}?view=true&backUrl=${encodeURIComponent(sanitizeRedirectUrl(path))}`
                        );
                      } else {
                        if (
                          (detailsProps.documentDetails.customers.length === 0 &&
                            detailsProps.documentDetails.employees.length === 0 &&
                            checkProfilePersonsAvailability(
                              detailsProps.documentDetails.category
                            )) ||
                          ('document_upload_html' in detailsProps.documentDetails &&
                            detailsProps.documentDetails.document_upload_html.length > 0)
                        ) {
                          setDocumentToSign(detailsProps.documentDetails);
                          setOpenAssignPersonDialog(true);
                        } else {
                          dispatch(showOverlay());
                          handleSignOriginalDocument(detailsProps.documentDetails);
                        }
                      }
                    }}
                    deleteDocument={() => {
                      setDocumentToDelete(detailsProps.documentDetails);
                      setDeleteMessageModalOpen(true);
                    }}
                    editDocument={(_) => {
                      handleTabChange(_, 0);
                      navigateToDocumentPageTab(
                        detailsProps.documentDetails,
                        0,
                        false,
                        false,
                        true
                      );
                      if (
                        detailsProps?.documentDetails &&
                        detailsProps?.documentDetails?.du_filetype &&
                        detailsProps?.documentDetails?.du_filetype[0] === 'pdf' //&&
                        /*detailsProps?.documentDetails?.document_upload_html.length === 0 &&*/
                        //!detailsProps?.documentUploadLatestHTMLId
                      ) {
                        if (localStorage.getItem('showPDFConversionMessage') === 'true') {
                          detailsProps.setPDFConversionMessage(true);
                        } else {
                          if (
                            detailsProps?.documentDetails?.document_upload_html.length === 0 &&
                            !detailsProps?.documentUploadLatestHTMLId
                          ) {
                            detailsProps.setPDFDocHTMLCreationAllowed(true);
                          }
                        }
                      }
                    }}
                    doc={detailsProps.documentDetails}
                    pendingStatus={detailsProps.loadingData}
                    allowGettingAISummary={detailsProps ? detailsProps.htmlRetrieved : false}
                  />
                </Box>
              </Grid>
            </>
          )}
        </Grid>
      </Box>
      {/*signingDialogOpen && (
        <MessageModal
          open={signingDialogOpen}
          modalMessage={t('messages.signingVersionMessage')}
          actionButtonText={t('buttons.signLatestVersion')}
          actionButtonFn={() =>
            handleSignLatestVersion(documentToSign, detailsProps?.documentUploadLatestHTMLId)
          }
          actionButtonAdditionalText={t('buttons.signOriginalDocument')}
          actionButtonAdditionalFn={() => handleSignOriginalDocument(documentToSign)}
          goBackButtonText={t('buttons.goBack')}
          document={documentToSign}
          onClose={() => {
            setDocumentToSign(null);
            setSigningDialogOpen(false);
          }}
        />
      )*/}
      {openAssignPersonDialog && documentToSign && (
        <AssignProfilePersonModal
          open={openAssignPersonDialog}
          onClose={() => {
            setOpenAssignPersonDialog(false);
            setDocumentToSign(null);
            setAssignee(null);
          }}
          modalTitle={t('headers.startSigning')}
          category={documentToSign?.category}
          document={documentToSign}
          goBackButtonText={t('buttons.goBack')}
          actionButtonText={t('buttons.signLatestVersion')}
          actionButtonTextSecondary={t('buttons.signOriginalDocument')}
          modalMessage={t('messages.signingRecommendations')}
          assignee={assignee}
          setAssignee={setAssignee}
          secondaryAction={() => {
            dispatch(showOverlay());
            if (assignee) {
              patchDocumentWithProfilePerson()
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                //@ts-ignore
                .then((res) => handleSignOriginalDocument(res))
                .catch(() => handleSignOriginalDocument(documentToSign));
            } else {
              handleSignOriginalDocument(documentToSign);
            }
          }}
          primaryAction={() => {
            dispatch(showOverlay());
            if (assignee) {
              patchDocumentWithProfilePerson()
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                //@ts-ignore
                .then((res) => handleSignLatestVersion(res))
                .catch(() => handleSignLatestVersion(documentToSign));
            } else {
              handleSignLatestVersion(documentToSign);
            }
          }}
        />
      )}
      {detailsProps.pdfConversionMessage && (
        <MessageModal
          open={detailsProps.pdfConversionMessage}
          modalContent={
            <Box sx={{ width: '100%' }}>
              <p>{t('messages.pdfConversionMessagePart1')}</p>
              <p>{t('messages.pdfConversionMessagePart2')}</p>
              <Box display={'flex'} alignItems={'flex-start'} className='checkbox-row'>
                <Checkbox
                  style={{ marginLeft: -10, paddingTop: 0, paddingBottom: 0 }}
                  id='dontShowPDFConversionMessage'
                  checked={detailsProps.dontShowPDFConversionMessage}
                  onChange={(event) => {
                    detailsProps.setDontShowPDFConversionMessage(event.target.checked);
                    localStorage.setItem(
                      'showPDFConversionMessage',
                      (!event.target.checked).toString()
                    );
                  }}
                />
                <label style={{ marginTop: 0 }} htmlFor='dontShowPDFConversionMessage'>
                  {t('labels.dontShowThisMessageAgain')}
                </label>
              </Box>
            </Box>
          }
          noActionButton={true}
          goBackButtonText={t('buttons.ok')}
          onClose={() => {
            if (
              detailsProps?.documentDetails?.document_upload_html.length === 0 &&
              !detailsProps?.documentUploadLatestHTMLId
            ) {
              detailsProps.setPDFDocHTMLCreationAllowed(true);
            }
            detailsProps.setPDFConversionMessage(false);
          }}
        />
      )}
      {detailsProps.pdfDocHTMLCreationAllowed && (
        <MessageModal
          open={detailsProps.pdfDocHTMLCreationAllowed}
          modalMessage={t('messages.creatingHTMLForPDF')}
          noButtons={true}
          modalContent={
            <Box sx={{ width: '100%', padding: '20px 0 0' }}>
              <ProgressBarFancy initialValue={0} finalValue={100} duration={30000} />
            </Box>
          }
          onClose={() => {
            detailsProps.setPDFDocHTMLCreationAllowed(false);
          }}
        />
      )}
      {detailsProps.errorModalOpen && (
        <MessageModal
          open={detailsProps.errorModalOpen}
          modalMessage={t('messages.firstDocumentConversionFail')}
          actionButtonText={t('buttons.retry')}
          actionButtonFn={() => {
            detailsProps.setErrorModalOpen(false);
            detailsProps.createDocumentHTML(Number(documentId));
          }}
          goBackButtonText={t('buttons.goBack')}
          onClose={() => {
            detailsProps.setErrorModalOpen(false);
          }}
        />
      )}
      {fileTypeModalOpen && documentForDownloading && (
        <FileTypeDownloadModal
          onClose={() => {
            handleCloseFileTypeModal();
          }}
          open={fileTypeModalOpen}
          fileName={fileNameForDownloading}
          fileType={fileTypeForDownloading}
          handleFileTypeChange={setFileTypeForDownloading}
          handleFileNameChange={setFileNameForDownloading}
          document={documentForDownloading}
          handleDownloadContract={() => {
            handleDownloadContract(documentForDownloading.id);
          }}
          handleDownloadDocument={() =>
            downloadDocumentUploadItem(documentForDownloading.id, documentForDownloading.name)
          }
          handleDownloadHTML={
            documentForDownloading?.du_filetype.includes('pdf')
              ? () => setPdfSaveDialogOpen(true)
              : () => {
                  handleDownloadHTML(
                    documentForDownloading?.document_upload_html.length > 0
                      ? Math.max(...documentForDownloading.document_upload_html)
                      : detailsProps?.documentUploadLatestHTMLId,
                    documentForDownloading.name.endsWith('.pdf') ||
                      documentForDownloading.name.endsWith('.docx') ||
                      documentForDownloading.name.endsWith('.doc')
                      ? documentForDownloading.name
                          .replace('.pdf', '')
                          .replace('.docx', '')
                          .replace('.doc', '')
                      : documentForDownloading.name,
                    documentForDownloading.du_filetype[0]
                  );
                }
          }
        />
      )}
      {pdfSaveDialogOpen && documentForDownloading && (
        <MessageModal
          open={pdfSaveDialogOpen}
          modalMessage={t('messages.chooseSaveAsPDF')}
          actionButtonText={t('buttons.ok')}
          actionButtonFn={() => {
            Helpers.convertHtmlToPdfAndDownload(
              documentHTML,
              documentForDownloading?.name.replace('.pdf', '')
            );
            setPdfSaveDialogOpen(false);
          }}
          goBackButtonText={t('buttons.goBack')}
          onClose={() => {
            setPdfSaveDialogOpen(false);
          }}
        />
      )}
      {deleteMessageModalOpen && (
        <MessageModal
          open={deleteMessageModalOpen}
          modalTitle={t('dialogTitles.deleteDocument')}
          modalMessage={t('messages.deleteDocumentDialog')}
          actionButtonText={t('buttons.goBack')}
          actionButtonFn={() => {
            setDocumentToDelete(null);
            setDeleteMessageModalOpen(false);
          }}
          goBackButtonText={t('buttons.delete')}
          document={documentToDelete}
          onClose={() => {
            handleDeleteDocument();
          }}
        />
      )}
      {(detailsProps.pendingStatus || downloadPendingStatus || documentDeletePendingStatus) && (
        <ProgressOverlay />
      )}
    </>
  );
};

export default memo(DocumentDetails);
